import React, { useState, useEffect, useCallback } from 'react';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import Icon from 'components/AppIcon';
import api from 'lib/api';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-[var(--color-warning)]/10 text-[var(--color-warning)]', icon: 'Clock' },
  completed: { label: 'Joined', color: 'bg-[var(--color-success)]/10 text-[var(--color-success)]', icon: 'CheckCircle2' },
  rewarded: { label: 'Rewarded', color: 'bg-[var(--color-accent)]/10 text-[var(--color-accent)]', icon: 'Gift' },
  expired: { label: 'Expired', color: 'bg-[var(--color-error)]/10 text-[var(--color-error)]', icon: 'XCircle' },
};

export default function QRCodeCard({ memberId, profileId, memberName }) {
  const [referralCode, setReferralCode] = useState('');
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);

  const fetchData = useCallback(async () => {
    if (!profileId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const { data: profileData } = await api.get('/profiles/me');
      setReferralCode(profileData?.referral_code ?? '');
      setReferrals([]);
    } catch (err) {
      setError('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const referralLink = `https://gymvendord7636.builtwithrocket.new/join?ref=${referralCode}`;
  const qrValue = memberId ? `GYMCHECKIN:${memberId}` : 'GYMCHECKIN:UNKNOWN';

  const completedCount = referrals?.filter(r => r?.status === 'completed' || r?.status === 'rewarded')?.length ?? 0;
  const rewardsEarned = Math.floor(completedCount / 3);
  const progressToNextReward = completedCount % 3;

  const handleCopy = () => {
    navigator.clipboard?.writeText(referralLink)?.catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareWhatsApp = () => {
    const msg = encodeURIComponent(`Join my gym with my referral code ${referralCode}! Sign up here: ${referralLink}`);
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };

  const handleDownloadQR = () => {
    const canvas = document.getElementById('qr-download-canvas');
    if (!canvas) return;
    const url = canvas?.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `checkin-qr-${memberId ?? 'member'}.png`;
    a?.click();
  };

  return (
    <>
      <div className="flex flex-col gap-6">
        {/* QR Check-In Section */}
        <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)]">
          <h3 className="text-base font-semibold text-[var(--color-foreground)] font-[var(--font-heading)] mb-4">Quick Check-In QR Code</h3>
          <div className="flex items-center gap-5">
            <div
              className="w-28 h-28 bg-white rounded-[var(--radius-sm)] p-2 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity border border-[var(--color-border)] shadow-sm"
              onClick={() => setShowQRModal(true)}
              title="Click to enlarge"
            >
              {memberId ? (
                <QRCodeSVG value={qrValue} size={96} level="M" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Icon name="QrCode" size={48} color="var(--color-muted-foreground)" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-[var(--color-foreground)] font-[var(--font-caption)] mb-1">Your Personal Check-In Code</p>
              <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-3">Scan at the gym entrance for instant check-in</p>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-bold text-[var(--color-foreground)] font-[var(--font-caption)] bg-[var(--color-muted)] px-2 py-1 rounded font-mono">
                  {memberId ? memberId?.substring(0, 8)?.toUpperCase() : '—'}
                </span>
                <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Member ID</span>
              </div>
              <button
                onClick={() => setShowQRModal(true)}
                className="flex items-center gap-1.5 px-3 py-2 bg-[var(--color-primary)] text-white rounded-[var(--radius-sm)] text-xs font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity"
              >
                <Icon name="Maximize2" size={12} color="#fff" />
                View & Download QR
              </button>
            </div>
          </div>
        </div>

        {/* Referral Program Section */}
        <div className="bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] rounded-[var(--radius-md)] p-5 text-white shadow-[var(--shadow-md)] relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-white/5 -translate-y-1/2 translate-x-1/2" />
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-1">
              <Icon name="Gift" size={16} color="rgba(255,255,255,0.8)" />
              <p className="text-white/70 text-xs font-medium font-[var(--font-caption)] uppercase tracking-wider">Your Referral Code</p>
            </div>
            {loading ? (
              <div className="h-8 w-40 bg-white/20 animate-pulse rounded mb-3" />
            ) : (
              <h3 className="text-2xl font-bold font-[var(--font-heading)] tracking-widest mb-1">{referralCode || '—'}</h3>
            )}
            <p className="text-white/70 text-xs font-[var(--font-caption)] mb-4">Earn 1 free month for every 3 successful referrals</p>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handleCopy}
                disabled={loading || !referralCode}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-[var(--radius-sm)] text-sm font-medium font-[var(--font-caption)] transition-all duration-200 disabled:opacity-50 ${
                  copied ? 'bg-white/30 text-white' : 'bg-white text-[var(--color-primary)] hover:bg-white/90'
                }`}
              >
                <Icon name={copied ? 'Check' : 'Copy'} size={14} color={copied ? '#fff' : 'var(--color-primary)'} />
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
              <button
                onClick={handleShareWhatsApp}
                disabled={loading || !referralCode}
                className="flex items-center gap-1.5 px-4 py-2 rounded-[var(--radius-sm)] text-sm font-medium font-[var(--font-caption)] bg-green-500 text-white hover:bg-green-600 transition-colors disabled:opacity-50"
              >
                <Icon name="MessageCircle" size={14} color="#fff" />
                Share on WhatsApp
              </button>
            </div>
          </div>
        </div>

        {/* Referral Stats */}
        <div className="grid grid-cols-3 gap-3">
          {loading ? (
            [1, 2, 3]?.map(i => (
              <div key={i} className="h-20 bg-[var(--color-muted)] animate-pulse rounded-[var(--radius-sm)]" />
            ))
          ) : (
            <>
              <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-sm)] p-4 text-center shadow-[var(--shadow-sm)]">
                <p className="text-2xl font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">{referrals?.length}</p>
                <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">Total Referrals</p>
              </div>
              <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-sm)] p-4 text-center shadow-[var(--shadow-sm)]">
                <p className="text-2xl font-bold text-[var(--color-success)] font-[var(--font-heading)]">{completedCount}</p>
                <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">Successful</p>
              </div>
              <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-sm)] p-4 text-center shadow-[var(--shadow-sm)]">
                <p className="text-2xl font-bold text-[var(--color-accent)] font-[var(--font-heading)]">{rewardsEarned}</p>
                <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">Rewards Earned</p>
              </div>
            </>
          )}
        </div>

        {/* Progress to Next Reward */}
        {!loading && (
          <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-4 shadow-[var(--shadow-sm)]">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Icon name="Trophy" size={16} color="var(--color-warning)" />
                <span className="text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)]">Progress to Next Reward</span>
              </div>
              <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{progressToNextReward}/3 referrals</span>
            </div>
            <div className="h-2.5 bg-[var(--color-muted)] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700 bg-[var(--color-warning)]"
                style={{ width: `${(progressToNextReward / 3) * 100}%` }}
              />
            </div>
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-2">
              {progressToNextReward === 0 && completedCount === 0
                ? 'Refer 3 friends to earn your first free month!'
                : progressToNextReward === 0
                ? `You've earned ${rewardsEarned} free month${rewardsEarned > 1 ? 's' : ''}! Keep referring to earn more.`
                : `${3 - progressToNextReward} more successful referral${3 - progressToNextReward !== 1 ? 's' : ''} to earn a free month`}
            </p>
          </div>
        )}

        {/* Referral History */}
        {!loading && (
          <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-[var(--color-foreground)] font-[var(--font-heading)]">Referral History</h3>
              {referrals?.length > 0 && (
                <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{referrals?.length} total</span>
              )}
            </div>
            {error ? (
              <div className="flex items-center justify-center gap-2 py-6">
                <Icon name="AlertCircle" size={16} color="var(--color-error)" />
                <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{error}</p>
              </div>
            ) : referrals?.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 py-8">
                <div className="w-14 h-14 rounded-full bg-[var(--color-muted)] flex items-center justify-center">
                  <Icon name="Users" size={24} color="var(--color-muted-foreground)" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)]">No referrals yet</p>
                  <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-1">Share your code to start earning rewards</p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {referrals?.map(ref => {
                  const cfg = STATUS_CONFIG?.[ref?.status] ?? STATUS_CONFIG?.pending;
                  const date = ref?.created_at
                    ? new Date(ref?.created_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : '—';
                  const email = ref?.referred_email ?? '';
                  const atIdx = email?.indexOf('@');
                  const maskedEmail = atIdx > 3
                    ? email?.substring(0, 3) + '***' + email?.substring(atIdx)
                    : email?.substring(0, 2) + '***';
                  return (
                    <div
                      key={ref?.id}
                      className="flex items-center gap-3 p-3 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] hover:border-[var(--color-primary)]/20 transition-colors"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${cfg?.color?.split(' ')?.[0]}`}>
                        <Icon name={cfg?.icon} size={15} color="currentColor" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[var(--color-foreground)] font-[var(--font-caption)] truncate">{maskedEmail}</p>
                        <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">{date}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {ref?.reward_granted && (
                          <span className="text-[10px] bg-[var(--color-accent)]/10 text-[var(--color-accent)] px-2 py-0.5 rounded-full font-semibold font-[var(--font-caption)]">
                            Rewarded
                          </span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium font-[var(--font-caption)] ${cfg?.color}`}>
                          {cfg?.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* QR Modal */}
      {showQRModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowQRModal(false)}>
          <div
            className="bg-[var(--color-card)] rounded-[var(--radius-md)] p-6 shadow-[var(--shadow-lg)] flex flex-col items-center gap-4 max-w-xs w-full mx-4"
            onClick={e => e?.stopPropagation()}
          >
            <div className="flex items-center justify-between w-full">
              <h3 className="text-base font-semibold text-[var(--color-foreground)] font-[var(--font-heading)]">Check-In QR Code</h3>
              <button onClick={() => setShowQRModal(false)} className="p-1.5 rounded hover:bg-[var(--color-muted)] transition-colors">
                <Icon name="X" size={16} color="var(--color-muted-foreground)" />
              </button>
            </div>
            <div className="bg-white p-4 rounded-[var(--radius-sm)]">
              <QRCodeCanvas id="qr-download-canvas" value={qrValue} size={200} level="H" />
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)]">{memberName ?? 'Member'}</p>
              <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">Show this QR to gym staff for check-in</p>
            </div>
            <button
              onClick={handleDownloadQR}
              className="flex items-center gap-2 w-full justify-center px-4 py-2.5 bg-[var(--color-primary)] text-white rounded-[var(--radius-sm)] text-sm font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity"
            >
              <Icon name="Download" size={15} color="#fff" />
              Download as PNG
            </button>
          </div>
        </div>
      )}
    </>
  );
}
